import { memo } from 'react';
import type { MindMapNode } from '@/model/types';
import type { NodeBounds } from '@/layout/types';
import type { ResizeHandle, ResizePreview } from './hooks/useNodeResize';
import { useTheme } from '@/theme/themeContext';
import { getStyleForLevel } from '@/theme/defaultTheme';
import { ImageThumbnail } from './ImageThumbnail';
import { commandRegistry } from '@/commands/registry';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';

function truncateUrl(url: string, maxLen: number): string {
  try {
    const parsed = new URL(url);
    const display = parsed.hostname + parsed.pathname;
    return display.length > maxLen ? display.slice(0, maxLen - 3) + '...' : display;
  } catch {
    return url.length > maxLen ? url.slice(0, maxLen - 3) + '...' : url;
  }
}

interface CollaboratorSelection {
  color: string;
  name: string;
}

interface Props {
  node: MindMapNode;
  bounds: NodeBounds;
  isSelected: boolean;
  isDropTarget?: boolean;
  isSearchMatch?: boolean;
  collaboratorSelections?: CollaboratorSelection[];
  isResizing?: boolean;
  resizePreviewSize?: ResizePreview | null;
  onClick: (nodeId: string, e: React.MouseEvent) => void;
  onDoubleClick: (nodeId: string, e: React.MouseEvent) => void;
  onMouseDown?: (nodeId: string, e: React.MouseEvent) => void;
  onContextMenu?: (nodeId: string, e: React.MouseEvent) => void;
  onResizeHandleMouseDown?: (nodeId: string, handle: ResizeHandle, e: React.MouseEvent) => void;
  onTouchStart?: (nodeId: string, e: React.TouchEvent) => void;
  onResizeHandleTouchStart?: (nodeId: string, handle: ResizeHandle, e: React.TouchEvent) => void;
  mapId?: string | null;
  onImagePreview?: (src: string, alt: string) => void;
  commentCount?: { total: number; unresolved: number };
}

export const NodeRenderer = memo(function NodeRenderer({
  node,
  bounds,
  isSelected,
  isDropTarget,
  isSearchMatch,
  collaboratorSelections,
  isResizing,
  resizePreviewSize,
  onClick,
  onDoubleClick,
  onMouseDown,
  onContextMenu,
  onResizeHandleMouseDown,
  onTouchStart,
  onResizeHandleTouchStart,
  mapId,
  onImagePreview,
  commentCount,
}: Props) {
  const theme = useTheme();
  const levelStyle = getStyleForLevel(theme, bounds.depth);

  const bg = node.style?.backgroundColor ?? levelStyle.backgroundColor;
  const textColor = node.style?.textColor ?? levelStyle.textColor;
  const fontSize = node.style?.fontSize ?? levelStyle.fontSize;
  const fontWeight = node.style?.fontWeight ?? levelStyle.fontWeight;
  const fontStyle = node.style?.fontStyle ?? 'normal';
  const textDecoration = node.style?.textDecoration ?? 'none';

  // Determine whether this node has extra content (links / image attachments)
  const hasLinks = node.links != null && node.links.length > 0;
  const imageAttachments =
    node.attachments?.filter(a => a.mimeType.startsWith('image/')).slice(0, 2) ?? [];
  const hasImages = imageAttachments.length > 0;
  const hasExtra = hasLinks || hasImages;

  // Text position: top-aligned when extra content present, centered otherwise
  const textY = hasExtra
    ? bounds.y + levelStyle.paddingY + fontSize * 0.6
    : bounds.centerY;

  // Where extra content starts (below the text, inside the rect)
  const extraStartY = bounds.y + levelStyle.paddingY + fontSize + 8;

  // Links layout
  const shownLinks = hasLinks ? Math.min(node.links!.length, 3) : 0;
  const hasMoreLinks = hasLinks && node.links!.length > 3;
  const linkBlockHeight = hasLinks
    ? shownLinks * 16 + (hasMoreLinks ? 14 : 0) + 4
    : 0;

  // Image badges start after links
  const imageBadgeStartY = extraStartY + linkBlockHeight;

  return (
    <g
      className="node"
      data-node-id={node.id}
      onClick={(e) => onClick(node.id, e)}
      onDoubleClick={(e) => onDoubleClick(node.id, e)}
      onMouseDown={onMouseDown ? (e) => onMouseDown(node.id, e) : undefined}
      onTouchStart={onTouchStart ? (e) => onTouchStart(node.id, e) : undefined}
      onContextMenu={onContextMenu ? (e) => onContextMenu(node.id, e) : undefined}
      style={{ cursor: 'pointer' }}
    >
      {/* Selection highlight */}
      {isSelected && (
        <rect
          x={bounds.x - 3}
          y={bounds.y - 3}
          width={bounds.width + 6}
          height={bounds.height + 6}
          rx={levelStyle.borderRadius + 2}
          ry={levelStyle.borderRadius + 2}
          fill="none"
          stroke={theme.selectionColor}
          strokeWidth={2}
        />
      )}

      {/* Drop target indicator */}
      {isDropTarget && (
        <rect
          x={bounds.x - 4}
          y={bounds.y - 4}
          width={bounds.width + 8}
          height={bounds.height + 8}
          rx={levelStyle.borderRadius + 3}
          ry={levelStyle.borderRadius + 3}
          fill="none"
          stroke="#d97706"
          strokeWidth={2}
          strokeDasharray="6 3"
        />
      )}

      {/* Search match highlight */}
      {isSearchMatch && !isSelected && (
        <rect
          x={bounds.x - 3}
          y={bounds.y - 3}
          width={bounds.width + 6}
          height={bounds.height + 6}
          rx={levelStyle.borderRadius + 2}
          ry={levelStyle.borderRadius + 2}
          fill="none"
          stroke={theme.searchHighlightColor}
          strokeWidth={2}
        />
      )}

      {/* Collaborator selection outlines */}
      {collaboratorSelections && collaboratorSelections.length > 0 && collaboratorSelections.map((cs, idx) => (
        <g key={cs.name}>
          <rect
            x={bounds.x - 6 - idx * 3}
            y={bounds.y - 6 - idx * 3}
            width={bounds.width + 12 + idx * 6}
            height={bounds.height + 12 + idx * 6}
            rx={levelStyle.borderRadius + 4 + idx * 2}
            ry={levelStyle.borderRadius + 4 + idx * 2}
            fill="none"
            stroke={cs.color}
            strokeWidth={2}
          />
          <rect
            x={bounds.x - 6 - idx * 3}
            y={bounds.y - 20 - idx * 3}
            width={Math.min(cs.name.length * 7 + 8, 80)}
            height={14}
            rx={3}
            ry={3}
            fill={cs.color}
          />
          <text
            x={bounds.x - 6 - idx * 3 + 4}
            y={bounds.y - 11 - idx * 3}
            fontSize={9}
            fontFamily="system-ui, sans-serif"
            fill="#fff"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {cs.name.length > 10 ? cs.name.slice(0, 10) + '...' : cs.name}
          </text>
        </g>
      ))}

      {/* Node background */}
      <rect
        x={bounds.x}
        y={bounds.y}
        width={bounds.width}
        height={bounds.height}
        rx={levelStyle.borderRadius}
        ry={levelStyle.borderRadius}
        fill={bg}
        stroke={node.style?.borderColor ?? 'none'}
        strokeWidth={node.style?.borderColor ? 1 : 0}
      />

      {/* Node text */}
      <text
        x={bounds.centerX}
        y={textY}
        textAnchor="middle"
        dominantBaseline={hasExtra ? 'auto' : 'central'}
        fill={textColor}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontStyle={fontStyle}
        textDecoration={textDecoration}
        fontFamily="system-ui, -apple-system, sans-serif"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {node.text || ' '}
      </text>

      {/* Collapse/expand indicator (clickable) */}
      {node.childIds.length > 0 && (
        <g
          onClick={(e) => {
            e.stopPropagation();
            const ctx = {
              doc: useDocumentStore.getState().mapDoc,
              editorState: useEditorStore.getState(),
            };
            // Ensure this node is treated as selected for the command
            const prevSelected = ctx.editorState.selectedNodeIds;
            if (!prevSelected.includes(node.id)) {
              useEditorStore.getState().selectNode(node.id, false);
              ctx.editorState = useEditorStore.getState();
            }
            commandRegistry.execute('node.toggleCollapse', ctx);
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            const ctx = {
              doc: useDocumentStore.getState().mapDoc,
              editorState: useEditorStore.getState(),
            };
            const prevSelected = ctx.editorState.selectedNodeIds;
            if (!prevSelected.includes(node.id)) {
              useEditorStore.getState().selectNode(node.id, false);
              ctx.editorState = useEditorStore.getState();
            }
            commandRegistry.execute('node.toggleCollapse', ctx);
          }}
          style={{ cursor: 'pointer' }}
        >
          <circle
            cx={bounds.x + bounds.width + 8}
            cy={bounds.centerY}
            r={7}
            fill={levelStyle.backgroundColor}
            stroke={theme.connectorStyle.color}
            strokeWidth={1}
          />
          {node.collapsed ? (
            /* Collapsed: show "+" icon */
            <>
              <line
                x1={bounds.x + bounds.width + 4}
                y1={bounds.centerY}
                x2={bounds.x + bounds.width + 12}
                y2={bounds.centerY}
                stroke={theme.connectorStyle.color}
                strokeWidth={1.5}
                strokeLinecap="round"
                style={{ pointerEvents: 'none' }}
              />
              <line
                x1={bounds.x + bounds.width + 8}
                y1={bounds.centerY - 4}
                x2={bounds.x + bounds.width + 8}
                y2={bounds.centerY + 4}
                stroke={theme.connectorStyle.color}
                strokeWidth={1.5}
                strokeLinecap="round"
                style={{ pointerEvents: 'none' }}
              />
            </>
          ) : (
            /* Expanded: show "-" icon */
            <line
              x1={bounds.x + bounds.width + 4}
              y1={bounds.centerY}
              x2={bounds.x + bounds.width + 12}
              y2={bounds.centerY}
              stroke={theme.connectorStyle.color}
              strokeWidth={1.5}
              strokeLinecap="round"
              style={{ pointerEvents: 'none' }}
            />
          )}
        </g>
      )}

      {/* Notes indicator (right side) */}
      {node.notes && (
        <circle
          cx={bounds.x + bounds.width - 6}
          cy={bounds.y + 6}
          r={5}
          fill={theme.selectionColor}
        >
          <title>Has notes</title>
        </circle>
      )}

      {/* Link indicator (small icon at top-left) */}
      {hasLinks && (
        <g transform={`translate(${bounds.x + 6}, ${bounds.y + 3})`}>
          <title>{`${node.links!.length} link${node.links!.length > 1 ? 's' : ''}`}</title>
          <path
            d="M4.5 1.5a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h1l1.5-1.5h-2.5a1.5 1.5 0 0 1 0-3h2.5L5.5 1.5h-1zm3 0L6 3h2.5a1.5 1.5 0 0 1 0 3H6l1.5 1.5h1a3 3 0 0 0 0-6h-1zM4 4.5h4v1H4v-1z"
            fill="#60a5fa"
            style={{ pointerEvents: 'none' }}
          />
        </g>
      )}

      {/* Attachment indicator (small icon at top-left, below link icon) */}
      {node.attachments && node.attachments.length > 0 && (
        <g
          transform={`translate(${bounds.x + 4}, ${
            bounds.y + (hasLinks ? 14 : 3)
          })`}
        >
          <title>{`${node.attachments.length} attachment${node.attachments.length > 1 ? 's' : ''}`}</title>
          <path
            d="M6 1L2.5 4.5a2.12 2.12 0 0 0 3 3L9 4a1.41 1.41 0 0 0-2-2L3.5 5.5a.71.71 0 0 0 1 1L8 3"
            fill="none"
            stroke="#a78bfa"
            strokeWidth={1.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ pointerEvents: 'none' }}
          />
        </g>
      )}

      {/* Comment indicator (top-left, below attachments/links) */}
      {commentCount && commentCount.total > 0 && (
        <g
          transform={`translate(${bounds.x + 4}, ${
            bounds.y +
            (hasLinks ? 14 : 3) +
            (node.attachments && node.attachments.length > 0 ? 12 : 0)
          })`}
        >
          <title>{`${commentCount.total} comment${commentCount.total > 1 ? 's' : ''}${commentCount.unresolved > 0 ? ` (${commentCount.unresolved} unresolved)` : ''}`}</title>
          {/* Speech bubble icon */}
          <path
            d="M1 1h8a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H5l-2 2V8H1a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z"
            fill={commentCount.unresolved > 0 ? '#f59e0b' : '#64748b'}
            style={{ pointerEvents: 'none' }}
          />
          {/* Badge with count */}
          {commentCount.unresolved > 0 && (
            <>
              <circle
                cx={10}
                cy={1}
                r={5}
                fill="#ef4444"
              />
              <text
                x={10}
                y={1}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={7}
                fontWeight={700}
                fill="#fff"
                fontFamily="system-ui, sans-serif"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {commentCount.unresolved > 9 ? '9+' : commentCount.unresolved}
              </text>
            </>
          )}
        </g>
      )}

      {/* Resize preview (dashed outline during resize drag) */}
      {isResizing && resizePreviewSize && (
        <rect
          x={bounds.x + resizePreviewSize.offsetX}
          y={bounds.y + resizePreviewSize.offsetY}
          width={resizePreviewSize.width}
          height={resizePreviewSize.height}
          rx={levelStyle.borderRadius}
          ry={levelStyle.borderRadius}
          fill="none"
          stroke={theme.selectionColor}
          strokeWidth={1.5}
          strokeDasharray="4 3"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Resize handles (only shown when selected) */}
      {isSelected && onResizeHandleMouseDown && (
        <>
          {/* Top-left corner handle */}
          <rect
            x={bounds.x - 3}
            y={bounds.y - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 'nw-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'top-left', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'top-left', e) : undefined}
          />
          {/* Top-center handle */}
          <rect
            x={bounds.centerX - 3}
            y={bounds.y - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 'n-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'top', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'top', e) : undefined}
          />
          {/* Top-right corner handle */}
          <rect
            x={bounds.x + bounds.width - 3}
            y={bounds.y - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 'ne-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'top-right', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'top-right', e) : undefined}
          />
          {/* Left-center handle */}
          <rect
            x={bounds.x - 3}
            y={bounds.centerY - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 'w-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'left', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'left', e) : undefined}
          />
          {/* Right-center handle */}
          <rect
            x={bounds.x + bounds.width - 3}
            y={bounds.centerY - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 'e-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'right', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'right', e) : undefined}
          />
          {/* Bottom-left corner handle */}
          <rect
            x={bounds.x - 3}
            y={bounds.y + bounds.height - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 'sw-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'bottom-left', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'bottom-left', e) : undefined}
          />
          {/* Bottom-center handle */}
          <rect
            x={bounds.centerX - 3}
            y={bounds.y + bounds.height - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 's-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'bottom', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'bottom', e) : undefined}
          />
          {/* Bottom-right corner handle */}
          <rect
            x={bounds.x + bounds.width - 3}
            y={bounds.y + bounds.height - 3}
            width={6}
            height={6}
            fill="#fff"
            stroke={theme.selectionColor}
            strokeWidth={1}
            style={{ cursor: 'se-resize' }}
            onMouseDown={(e) => onResizeHandleMouseDown(node.id, 'bottom-right', e)}
            onTouchStart={onResizeHandleTouchStart ? (e) => onResizeHandleTouchStart(node.id, 'bottom-right', e) : undefined}
          />
        </>
      )}

      {/* Inline link rows inside the node rect */}
      {hasLinks && (
        <g>
          {node.links!.slice(0, 3).map((link, i) => (
            <g
              key={link.url}
              onClick={(e) => {
                e.stopPropagation();
                window.open(link.url, '_blank');
              }}
              style={{ cursor: 'pointer' }}
            >
              {/* Small link icon */}
              <path
                d="M4.5 1.5a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h1l1.5-1.5h-2.5a1.5 1.5 0 0 1 0-3h2.5L5.5 1.5h-1zm3 0L6 3h2.5a1.5 1.5 0 0 1 0 3H6l1.5 1.5h1a3 3 0 0 0 0-6h-1zM4 4.5h4v1H4v-1z"
                fill="#60a5fa"
                transform={`translate(${bounds.x + 6}, ${extraStartY + i * 16})`}
                style={{ pointerEvents: 'none' }}
              />
              <text
                x={bounds.x + 20}
                y={extraStartY + 9 + i * 16}
                fontSize={11}
                fill="#60a5fa"
                fontFamily="system-ui, sans-serif"
                textDecoration="underline"
                style={{ pointerEvents: 'auto' }}
              >
                {link.label || truncateUrl(link.url, 35)}
              </text>
            </g>
          ))}
          {hasMoreLinks && (
            <text
              x={bounds.x + 20}
              y={extraStartY + 9 + 3 * 16}
              fontSize={10}
              fill="#94a3b8"
              fontFamily="system-ui, sans-serif"
              style={{ pointerEvents: 'none' }}
            >
              +{node.links!.length - 3} more link{node.links!.length - 3 > 1 ? 's' : ''}
            </text>
          )}
        </g>
      )}

      {/* Image thumbnails inside the node rect, below links */}
      {hasImages && mapId && onImagePreview && (
        <g>
          {imageAttachments.map((att, i) => {
            const thumbWidth = Math.min(bounds.width - 8, 100);
            const thumbHeight = 60;
            return (
              <ImageThumbnail
                key={att.id}
                mapId={mapId}
                attachmentId={att.id}
                s3Key={att.s3Key}
                fileName={att.fileName}
                x={bounds.x + 4}
                y={imageBadgeStartY + i * (thumbHeight + 4)}
                width={thumbWidth}
                height={thumbHeight}
                borderRadius={4}
                onClickPreview={onImagePreview}
              />
            );
          })}
        </g>
      )}

      {/* Non-image attachment download badges */}
      {node.attachments && node.attachments.filter(a => !a.mimeType.startsWith('image/')).length > 0 && (
        <g>
          {node.attachments.filter(a => !a.mimeType.startsWith('image/')).slice(0, 2).map((att, i) => {
            const nonImageStartY = imageBadgeStartY + (hasImages ? imageAttachments.length * 22 : 0);
            return (
              <g key={att.id}>
                <rect
                  x={bounds.x + 4}
                  y={nonImageStartY + i * 22}
                  width={Math.min(bounds.width - 8, att.fileName.length * 6 + 28)}
                  height={18}
                  rx={4}
                  fill="#1e293b"
                  stroke="#475569"
                  strokeWidth={0.5}
                />
                {/* Download icon */}
                <path
                  d={`M${bounds.x + 10} ${nonImageStartY + 5 + i * 22} v6 l-3-3 M${bounds.x + 10} ${nonImageStartY + 11 + i * 22} l3-3 M${bounds.x + 7} ${nonImageStartY + 14 + i * 22} h6`}
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth={1}
                  strokeLinecap="round"
                />
                <text
                  x={bounds.x + 22}
                  y={nonImageStartY + 12 + i * 22}
                  fontSize={10}
                  fill="#cbd5e1"
                  fontFamily="system-ui, sans-serif"
                  style={{ pointerEvents: 'none' }}
                >
                  {att.fileName.length > 25 ? att.fileName.slice(0, 22) + '...' : att.fileName}
                </text>
              </g>
            );
          })}
        </g>
      )}
    </g>
  );
});
